import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, DatePicker, Popconfirm, InputNumber,
} from 'antd'
import { SwapOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const SHIFTS = ['A', 'B', 'C']

export default function ShiftHandoverPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [dateFilter, setDateFilter] = useState<string>('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: handovers, isLoading } = useQuery({
    queryKey: ['shift-handovers', plantId, dateFilter],
    queryFn: () =>
      apiClient.get(`${API}/shift-handovers`, {
        params: { plant_id: plantId, date: dateFilter || undefined },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/shift-handovers`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-handovers'] })
      message.success('Handover created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const acceptMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/shift-handovers/${id}/accept`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-handovers'] })
      message.success('Handover accepted')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const list = Array.isArray(handovers) ? handovers : []

  const columns = [
    {
      title: 'Handover No',
      dataIndex: 'handover_no',
      key: 'handover_no',
      width: 130,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Date',
      dataIndex: 'handover_date',
      key: 'handover_date',
      width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YYYY') : '—',
    },
    { title: 'Outgoing Shift', dataIndex: 'outgoing_shift', key: 'outgoing_shift', width: 110 },
    { title: 'Incoming Shift', dataIndex: 'incoming_shift', key: 'incoming_shift', width: 110 },
    { title: 'Outgoing Incharge', dataIndex: 'outgoing_incharge', key: 'outgoing_incharge', width: 150 },
    { title: 'Incoming Incharge', dataIndex: 'incoming_incharge', key: 'incoming_incharge', width: 150 },
    {
      title: 'Visitors Inside',
      dataIndex: 'visitors_inside',
      key: 'visitors_inside',
      width: 110,
      render: (v: number) => (
        <Tag color={v > 0 ? 'orange' : 'default'}>{v ?? 0}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => (
        <Tag color={v === 'ACCEPTED' ? 'success' : 'processing'}>{v}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_: unknown, r: any) =>
        r.status === 'DRAFT' ? (
          <Popconfirm
            title="Accept this handover?"
            onConfirm={() => acceptMut.mutate(r.id)}
            okText="Accept"
          >
            <Button size="small" type="primary">Accept</Button>
          </Popconfirm>
        ) : null,
    },
  ]

  const expandedRowRender = (record: any) => (
    <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 4 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Typography.Text strong>Pending Incidents: </Typography.Text>
          <Typography.Text>{record.pending_incidents || '—'}</Typography.Text>
        </Col>
        <Col span={12}>
          <Typography.Text strong>Pending Passes: </Typography.Text>
          <Typography.Text>{record.pending_passes || '—'}</Typography.Text>
        </Col>
        <Col span={8} style={{ marginTop: 8 }}>
          <Typography.Text strong>Cameras Down: </Typography.Text>
          <Typography.Text>{record.cameras_down || '—'}</Typography.Text>
        </Col>
        <Col span={8} style={{ marginTop: 8 }}>
          <Typography.Text strong>Keys Pending: </Typography.Text>
          <Typography.Text>{record.keys_pending || '—'}</Typography.Text>
        </Col>
        <Col span={8} style={{ marginTop: 8 }}>
          <Typography.Text strong>Special Instructions: </Typography.Text>
          <Typography.Text>{record.special_instructions || '—'}</Typography.Text>
        </Col>
      </Row>
    </div>
  )

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <SwapOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Shift Handover
      </Title>
      <Typography.Text type="secondary">Shift handover register</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
          <Col>
            <Space wrap>
              <Select
                value={plantId}
                onChange={setPlantId}
                style={{ width: 200 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
              />
              <DatePicker
                value={dateFilter ? dayjs(dateFilter) : null}
                onChange={d => setDateFilter(d ? d.format('YYYY-MM-DD') : '')}
                allowClear
                placeholder="Filter by date"
              />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { form.resetFields(); setDrawerOpen(true) }}
            >
              Create Handover
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          scroll={{ x: 1100 }}
          expandable={{ expandedRowRender }}
        />
      </Card>

      <Drawer
        title="Create Shift Handover"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={500}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals =>
                    createMut.mutate({ ...vals, plant_id: plantId })
                  )
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical" initialValues={{ visitors_inside: 0 }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="outgoing_shift" label="Outgoing Shift" rules={[{ required: true }]}>
                <Select options={SHIFTS.map(s => ({ value: s, label: `Shift ${s}` }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="incoming_shift" label="Incoming Shift" rules={[{ required: true }]}>
                <Select options={SHIFTS.map(s => ({ value: s, label: `Shift ${s}` }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="outgoing_incharge" label="Outgoing Incharge" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="incoming_incharge" label="Incoming Incharge" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="visitors_inside" label="Visitors Inside (count)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="pending_incidents" label="Pending Incidents">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="pending_passes" label="Pending Passes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="cameras_down" label="Cameras Down">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="keys_pending" label="Keys Pending">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="special_instructions" label="Special Instructions">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
